import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { BookOpen, Plus, RefreshCw, Search, TrendingUp, RotateCcw, Archive, ChevronDown, ChevronUp, Pencil, Save, X } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────

interface PlaybookEntry {
  id: string;
  name: string;
  category: string;
  description: string | null;
  trigger_condition: string | null;
  action_template: string | null;
  expected_outcome: string | null;
  confidence_level: string;
  used_count: number;
  success_rate: number | null;
  status: string;
  applies_to: { roles?: string[]; stages?: string[] };
  source_experiment_id: string | null;
  playbook_learnings: string | null;
  created_at: string;
}

// ── Constants ─────────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'activation', label: 'Activation',  desc: 'Getting new users started',        color: 'text-blue-500'  },
  { value: 'retention',  label: 'Retention',   desc: 'Keeping active users growing',      color: 'text-green-500' },
  { value: 'expansion',  label: 'Expansion',   desc: 'Growing seats or usage',            color: 'text-purple-500'},
  { value: 'rescue',     label: 'Rescue',      desc: 'Re-engaging dropped users',         color: 'text-orange-500'},
  { value: 'other',      label: 'Other',       desc: 'Miscellaneous techniques',          color: 'text-muted-foreground' },
];

const CONFIDENCE_META: Record<string, { label: string; color: string }> = {
  high:   { label: 'High confidence',   color: 'bg-green-500/10 text-green-600 border-green-500/30' },
  medium: { label: 'Medium confidence', color: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30' },
  low:    { label: 'Low confidence',    color: 'bg-muted text-muted-foreground' },
};

// ── Main component ────────────────────────────────────────────────

export default function PlaybookBuilder() {
  const navigate    = useNavigate();
  const [params]    = useSearchParams();

  const [entries, setEntries]   = useState<PlaybookEntry[]>([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);

  // Edit form state (shared for create & edit)
  const [form, setForm] = useState({
    name: '', category: 'activation', description: '', trigger_condition: '',
    action_template: '', expected_outcome: '', confidence_level: 'medium',
    used_count: '0', success_rate: '', status: 'active',
    playbook_learnings: '', roles: '', source_experiment_id: '',
  });
  const [showForm, setShowForm] = useState(false);
  const [formSaving, setFormSaving] = useState(false);

  // Apply to client dialog
  const [applying, setApplying]   = useState<PlaybookEntry | null>(null);
  const [applyOrg, setApplyOrg]   = useState('');
  const [orgs, setOrgs]           = useState<{ id: string; name: string }[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const [entryRes, orgRes] = await Promise.all([
      supabase.from('cosmo_playbook_entries').select('*').order('category').order('confidence_level').order('created_at', { ascending: false }),
      supabase.from('organizations').select('id, name').order('name'),
    ]);
    setEntries(entryRes.data?.map(e => ({ ...e, applies_to: (e.applies_to ?? {}) as unknown as PlaybookEntry['applies_to'] })) as unknown as PlaybookEntry[] ?? []);
    setOrgs((orgRes.data ?? []).map(o => ({ id: o.id, name: o.name })));
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const fromExp = params.get('from_experiment');
    if (fromExp) {
      setForm(prev => ({ ...prev, source_experiment_id: fromExp }));
      setShowForm(true);
    }
  }, [load, params]);

  const toggleExpand = (id: string) =>
    setExpanded(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: '', category: 'activation', description: '', trigger_condition: '', action_template: '', expected_outcome: '', confidence_level: 'medium', used_count: '0', success_rate: '', status: 'active', playbook_learnings: '', roles: '', source_experiment_id: '' });
    setShowForm(true);
  };

  const openEdit = (e: PlaybookEntry) => {
    setEditingId(e.id);
    setForm({
      name: e.name, category: e.category, description: e.description ?? '',
      trigger_condition: e.trigger_condition ?? '', action_template: e.action_template ?? '',
      expected_outcome: e.expected_outcome ?? '', confidence_level: e.confidence_level,
      used_count: String(e.used_count), success_rate: e.success_rate != null ? String(e.success_rate) : '',
      status: e.status, playbook_learnings: e.playbook_learnings ?? '',
      roles: e.applies_to?.roles?.join(', ') ?? '', source_experiment_id: e.source_experiment_id ?? '',
    });
    setShowForm(true);
  };

  const saveForm = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setFormSaving(true);
    const payload = {
      name: form.name.trim(),
      category: form.category,
      description: form.description.trim() || null,
      trigger_condition: form.trigger_condition.trim() || null,
      action_template: form.action_template.trim() || null,
      expected_outcome: form.expected_outcome.trim() || null,
      confidence_level: form.confidence_level,
      used_count: parseInt(form.used_count) || 0,
      success_rate: form.success_rate ? parseFloat(form.success_rate) : null,
      status: form.status,
      playbook_learnings: form.playbook_learnings.trim() || null,
      applies_to: { roles: form.roles ? form.roles.split(',').map(s => s.trim()).filter(Boolean) : [] },
      source_experiment_id: form.source_experiment_id || null,
    };
    const { error } = editingId
      ? await supabase.from('cosmo_playbook_entries').update(payload).eq('id', editingId)
      : await supabase.from('cosmo_playbook_entries').insert(payload);
    setFormSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(editingId ? 'Entry updated' : 'Entry added to playbook');
    setShowForm(false);
    load();
  };

  const retireEntry = async (id: string) => {
    const { error } = await supabase.from('cosmo_playbook_entries').update({ status: 'retired' }).eq('id', id);
    if (error) { toast.error(error.message); return; }
    toast.success('Entry retired');
    load();
  };

  const applyToClient = () => {
    if (!applying || !applyOrg) return;
    navigate(`/admin/growth/experiments?prefill_org=${applyOrg}&prefill_playbook=${applying.id}`);
  };

  // Filter
  const filtered = entries.filter(e => {
    const q = search.toLowerCase();
    return !q || e.name.toLowerCase().includes(q) || (e.description ?? '').toLowerCase().includes(q);
  });

  // Group by category, put retired at end
  const active  = filtered.filter(e => e.status !== 'retired');
  const retired = filtered.filter(e => e.status === 'retired');

  const grouped = CATEGORIES.map(cat => ({
    ...cat,
    items: active.filter(e => e.category === cat.value),
  })).filter(g => g.items.length > 0);

  const EntryCard = ({ entry }: { entry: PlaybookEntry }) => {
    const conf   = CONFIDENCE_META[entry.confidence_level] ?? CONFIDENCE_META.medium;
    const isOpen = expanded.has(entry.id);

    return (
      <Card className="overflow-hidden">
        <div
          className="flex items-start justify-between p-4 cursor-pointer hover:bg-muted/20"
          onClick={() => toggleExpand(entry.id)}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Badge variant="outline" className={`text-xs ${conf.color}`}>{conf.label}</Badge>
              {entry.success_rate != null && (
                <span className="text-xs text-muted-foreground">{entry.success_rate}% success rate</span>
              )}
              {entry.used_count > 0 && (
                <span className="text-xs text-muted-foreground">Used {entry.used_count}×</span>
              )}
              {entry.applies_to?.roles?.length > 0 && (
                <span className="text-xs text-muted-foreground">· {entry.applies_to.roles.join(', ')}</span>
              )}
            </div>
            <p className="font-medium text-sm">{entry.name}</p>
            {entry.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{entry.description}</p>}
          </div>
          <div className="shrink-0 ml-3">
            {isOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>

        {isOpen && (
          <div className="border-t border-border/50 px-4 pb-4 pt-3 space-y-3">
            {entry.trigger_condition && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">When to use</p>
                <p className="text-sm">{entry.trigger_condition}</p>
              </div>
            )}
            {entry.action_template && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">What to do</p>
                <p className="text-sm">{entry.action_template}</p>
              </div>
            )}
            {entry.expected_outcome && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Expected outcome</p>
                <p className="text-sm text-muted-foreground">{entry.expected_outcome}</p>
              </div>
            )}
            {entry.playbook_learnings && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Learnings</p>
                <p className="text-sm text-muted-foreground">{entry.playbook_learnings}</p>
              </div>
            )}
            <Separator />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => { setApplying(entry); setApplyOrg(''); }}>
                Apply to client
              </Button>
              <Button size="sm" variant="outline" onClick={() => openEdit(entry)}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" />Edit
              </Button>
              {entry.status !== 'retired' && (
                <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => retireEntry(entry.id)}>
                  <Archive className="h-3.5 w-3.5 mr-1.5" />Retire
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Added {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}</p>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="p-6 space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Engagement Playbook</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Proven techniques, codified from experiments — ready to apply</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />Add Entry
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9" placeholder="Search playbook…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <BookOpen className="h-5 w-5 animate-pulse mr-2" /><span className="text-sm">Loading playbook…</span>
        </div>
      ) : active.length === 0 && retired.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <p className="font-medium mb-1">Playbook is empty</p>
            <p className="text-sm text-muted-foreground mb-4">Complete experiments to generate playbook entries, or add one manually.</p>
            <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1.5" />Add Entry</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {grouped.map(group => (
            <div key={group.value}>
              <div className="flex items-center gap-2 mb-3">
                <h2 className={`text-sm font-semibold uppercase tracking-wide ${group.color}`}>{group.label}</h2>
                <span className="text-xs text-muted-foreground">— {group.desc}</span>
                <Badge variant="outline" className="text-xs ml-auto">{group.items.length}</Badge>
              </div>
              <div className="space-y-2">{group.items.map(e => <EntryCard key={e.id} entry={e} />)}</div>
            </div>
          ))}

          {retired.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Retired</h2>
                <Badge variant="outline" className="text-xs">{retired.length}</Badge>
              </div>
              <div className="space-y-2 opacity-60">{retired.map(e => <EntryCard key={e.id} entry={e} />)}</div>
            </div>
          )}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? 'Edit Entry' : 'New Playbook Entry'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Sales L0→L1: Role-Matched Prompt Drop" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Confidence</Label>
                <Select value={form.confidence_level} onValueChange={v => setForm(p => ({ ...p, confidence_level: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief summary of this technique…" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">When to use (trigger condition)</Label>
              <Input value={form.trigger_condition} onChange={e => setForm(p => ({ ...p, trigger_condition: e.target.value }))} placeholder="e.g. New Sales user at L0" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">What to do (action template)</Label>
              <Textarea rows={2} value={form.action_template} onChange={e => setForm(p => ({ ...p, action_template: e.target.value }))} placeholder="e.g. Send role-matched prompt, no copy, no explanation." />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Expected outcome</Label>
              <Input value={form.expected_outcome} onChange={e => setForm(p => ({ ...p, expected_outcome: e.target.value }))} placeholder="e.g. 75% try it within 48 hours" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Times used</Label>
                <Input type="number" min={0} value={form.used_count} onChange={e => setForm(p => ({ ...p, used_count: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Success rate (%)</Label>
                <Input type="number" min={0} max={100} value={form.success_rate} onChange={e => setForm(p => ({ ...p, success_rate: e.target.value }))} placeholder="—" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="retired">Retired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Applies to roles (comma-separated)</Label>
              <Input value={form.roles} onChange={e => setForm(p => ({ ...p, roles: e.target.value }))} placeholder="e.g. Sales, Marketing" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Learnings</Label>
              <Textarea rows={2} value={form.playbook_learnings} onChange={e => setForm(p => ({ ...p, playbook_learnings: e.target.value }))} placeholder="What we learned from running this…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={saveForm} disabled={formSaving}>{formSaving ? 'Saving…' : editingId ? 'Save Changes' : 'Add to Playbook'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply to client dialog */}
      <Dialog open={!!applying} onOpenChange={() => setApplying(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Apply to Client</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{applying?.name}</p>
            <div className="space-y-1.5">
              <Label className="text-xs">Select org</Label>
              <Select value={applyOrg} onValueChange={setApplyOrg}>
                <SelectTrigger><SelectValue placeholder="Select org…" /></SelectTrigger>
                <SelectContent>{orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">This will open the Experiment Engine pre-filled with this playbook entry for the selected org.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplying(null)}>Cancel</Button>
            <Button onClick={applyToClient} disabled={!applyOrg}>Go to Experiments →</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
